import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { Shield, Bell, Lock, Palette, HelpCircle, MapPin, BookOpen, Info, ChevronLeft, Languages, Trash2 } from 'lucide-react';
import SubscriptionView from './SubscriptionView';
import NotificationsView from './NotificationsView';
import PrivacyView from './PrivacyView';
import ThemeView from './ThemeView';
import DiscoveryView from './DiscoveryView';
import TermsOfServiceView from './TermsOfServiceView';
import PrivacyPolicyView from './PrivacyPolicyView';
import BugReportView from './BugReportView';
import AppInfoView from './AppInfoView';
import LanguageView from './LanguageView';
import DeleteAccountView from './DeleteAccountView';

const settingsSections = [
    { id: 'subscription', icon: <Shield size={20} />, label: 'Harmony Pro', component: <SubscriptionView /> },
    { id: 'notifications', icon: <Bell size={20} />, label: 'Notifications', component: <NotificationsView /> },
    { id: 'privacy', icon: <Lock size={20} />, label: 'Privacy & Security', component: <PrivacyView /> },
    { id: 'language', icon: <Languages size={20} />, label: 'Language', component: <LanguageView /> },
    { id: 'theme', icon: <Palette size={20} />, label: 'Theme', component: <ThemeView /> },
    { id: 'discovery', icon: <MapPin size={20} />, label: 'Location & Discovery', component: <DiscoveryView /> },
    { id: 'terms', icon: <BookOpen size={20} />, label: 'Terms of Service', component: <TermsOfServiceView /> },
    { id: 'policy', icon: <BookOpen size={20} />, label: 'Privacy Policy', component: <PrivacyPolicyView /> },
    { id: 'support', icon: <HelpCircle size={20} />, label: 'Support', component: <BugReportView /> },
    { id: 'about', icon: <Info size={20} />, label: 'About', component: <AppInfoView /> },
    { id: 'delete', icon: <Trash2 size={20} />, label: 'Delete Account', component: <DeleteAccountView /> },
];

const Settings = () => {
    const [activeSection, setActiveSection] = useState('subscription');
    // State to control mobile view (show list or detail)
    const [showDetail, setShowDetail] = useState(false);

    const handleSectionSelect = (sectionId) => {
        setActiveSection(sectionId);
        setShowDetail(true); // On mobile, switch to detail view
    };

    const renderContent = () => {
        const activeComponent = settingsSections.find(sec => sec.id === activeSection)?.component;
        return activeComponent || <div>Select a section</div>;
    };

    const listVariants = {
        hidden: { x: '-100%', opacity: 0 },
        visible: { x: '0%', opacity: 1 },
        exit: { x: '-100%', opacity: 0 },
    };

    const detailVariants = {
        hidden: { x: '100%', opacity: 0 },
        visible: { x: '0%', opacity: 1 },
        exit: { x: '100%', opacity: 0 },
    };

    return (
        <div className="flex flex-col md:flex-row min-h-screen text-white font-sans overflow-hidden">
            {/* --- Desktop Navigation Panel --- */}
            <aside className="hidden md:flex w-64 bg-black/20 backdrop-blur-md p-4 flex-col border-r border-slate-700/50">
                <h1 className="text-3xl font-bold font-playfair mb-8 text-blue-300">Settings</h1>
                <nav className="flex flex-col space-y-2">
                    {settingsSections.map((section) => {
                        const isDelete = section.id === 'delete';
                        const isActive = activeSection === section.id;

                        return (
                            <button
                                key={section.id}
                                onClick={() => setActiveSection(section.id)}
                                className={`flex items-center space-x-4 p-3 rounded-lg transition-all duration-200 text-left ${
                                    isActive
                                        ? isDelete
                                            ? 'bg-red-500/30 text-white shadow-glow-red border border-red-500/50'
                                            : 'bg-blue-500/30 text-white shadow-glow-blue border border-blue-500/50'
                                        : isDelete
                                            ? 'hover:bg-red-700/50 text-red-400'
                                            : 'hover:bg-slate-700/50 text-gray-300'
                                }`}
                            >
                                <span className={`w-6 h-6 ${isDelete ? 'text-red-400' : 'text-blue-300'}`}>{section.icon}</span>
                                <span className="font-medium">{section.label}</span>
                            </button>
                        );
                    })}
                </nav>
            </aside>

            {/* --- Mobile View Container --- */}
            <div className="md:hidden flex-1 flex flex-col">
                <AnimatePresence initial={false} mode="wait">
                    {!showDetail ? (
                        <motion.div
                            key="list"
                            variants={listVariants}
                            initial="visible"
                            exit="exit"
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="p-4"
                        >
                            <h1 className="text-3xl font-bold font-playfair mb-6 text-center text-blue-300">Settings</h1>
                            <nav className="flex flex-col space-y-3">
                                {settingsSections.map((section) => {
                                    const isDelete = section.id === 'delete';
                                    return (
                                        <button
                                            key={section.id}
                                            onClick={() => handleSectionSelect(section.id)}
                                            className={`flex items-center space-x-4 p-4 rounded-lg transition-colors duration-200 ${
                                                isDelete
                                                    ? 'bg-red-800/50 border border-red-700/50 hover:bg-red-700/70'
                                                    : 'bg-slate-800/50 border border-slate-700/50 hover:bg-slate-700/70'
                                            }`}
                                        >
                                            <span className={`w-6 h-6 ${isDelete ? 'text-red-400' : 'text-blue-300'}`}>{section.icon}</span>
                                            <span className={`font-medium text-lg ${isDelete ? 'text-red-300' : ''}`}>{section.label}</span>
                                        </button>
                                    );
                                })}
                            </nav>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="detail"
                            variants={detailVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="flex-1 flex flex-col"
                        >
                            <div className="p-4 bg-black/20 backdrop-blur-md border-b border-slate-700/50 flex items-center">
                                <button onClick={() => setShowDetail(false)} className="p-2 mr-2 rounded-full hover:bg-slate-700/50">
                                    <ChevronLeft size={24} />
                                </button>
                                <h2 className="text-xl font-bold font-playfair text-blue-300">
                                    {settingsSections.find(s => s.id === activeSection)?.label}
                                </h2>
                            </div>
                            <main className="flex-1 p-4 overflow-y-auto">
                                {renderContent()}
                            </main>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* --- Desktop Content Panel --- */}
            <main className="hidden md:flex flex-1 p-10 overflow-y-auto">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeSection}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.2 }}
                    >
                        {renderContent()}
                    </motion.div>
                </AnimatePresence>
            </main>
        </div>
    );
};

export default Settings;